import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import type { EventoDataType } from "@/types/events"
import { useCalendarIntegration } from "@/modules/Calendar/hooks/useCalendarIntegration"
import {
  addEventToGoogleCalendar,
  addEventToMicrosoftCalendar,
  isGoogleConnected,
  isMicrosoftConnected,
} from "@/modules/Calendar/service/CalendarService"
import { useToast } from "@/contexts/ToastContext"
import { format, parseISO } from "date-fns"
import { ptBR } from "date-fns/locale"

interface CalendarSyncModalProps {
  isOpen: boolean
  evento: EventoDataType | null
  onClose: () => void
}

export function CalendarSyncModal({ isOpen, evento, onClose }: CalendarSyncModalProps) {
  const [syncing, setSyncing] = useState<"google" | "microsoft" | null>(null)
  const {
    googleConnected,
    microsoftConnected,
    loadingGoogle,
    loadingMicrosoft,
    connectGoogle,
    connectMicrosoft,
    disconnectGoogle,
    disconnectMicrosoft,
  } = useCalendarIntegration()

  const toast = useToast()

  const isBusy = !!syncing || loadingGoogle || loadingMicrosoft

  async function handleSyncGoogle() {
    if (!evento) return

    if (!googleConnected) {
      try {
        await connectGoogle()
      } catch (err: any) {
        toast.danger(err.message || "Erro ao conectar ao Google Calendar")
        return
      }
    }

    setSyncing("google")
    try {
      await addEventToGoogleCalendar(evento)
      toast.success("Evento adicionado ao Google Calendar!")
      onClose()
    } catch (err: any) {
      toast.danger(err.message || "Erro ao sincronizar com Google Calendar")
      if (!isGoogleConnected()) disconnectGoogle()
    } finally {
      setSyncing(null)
    }
  }

  async function handleSyncMicrosoft() {
    if (!evento) return

    if (!microsoftConnected) {
      try {
        await connectMicrosoft()
      } catch (err: any) {
        toast.danger(err.message || "Erro ao conectar ao Microsoft Calendar")
        return
      }
    }

    setSyncing("microsoft")
    try {
      await addEventToMicrosoftCalendar(evento)
      toast.success("Evento adicionado ao Microsoft Calendar!")
      onClose()
    } catch (err: any) {
      toast.danger(err.message || "Erro ao sincronizar com Microsoft Calendar")
      if (!isMicrosoftConnected()) disconnectMicrosoft()
    } finally {
      setSyncing(null)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isBusy) onClose()
      }}
    >
      <DialogContent className="border-white/10 bg-zinc-900 text-white sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <i className="fi fi-ts-calendar-plus flex items-center justify-center text-xl text-primary" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-white">
                Adicionar à agenda
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Escolha onde salvar este evento.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {evento && (
          <div className="rounded-lg border border-white/10 bg-white/5 p-3">
            <p className="font-semibold text-white">{evento.descricao}</p>
            <p className="mt-1 text-sm text-zinc-400">
              {format(parseISO(evento.data_inicio), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              {" · "}
              {format(parseISO(evento.data_inicio), "HH:mm", { locale: ptBR })}
              {evento.local ? ` · ${evento.local}` : ""}
            </p>
          </div>
        )}

        <div className="space-y-2.5">
          <button
            onClick={handleSyncGoogle}
            disabled={isBusy}
            className="flex w-full items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-left transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingGoogle || syncing === "google" ? (
              <div className="size-5 shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <svg
                viewBox="0 0 24 24"
                className="size-5 shrink-0"
                fill="none"
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-white">
                {googleConnected
                  ? "Adicionar ao Google Calendar"
                  : "Conectar e adicionar ao Google Calendar"}
              </p>
              {!googleConnected && (
                <p className="text-xs text-zinc-500">Será solicitada autorização de acesso</p>
              )}
            </div>
            {googleConnected && (
              <span className="shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
                conectado
              </span>
            )}
          </button>

          <button
            onClick={handleSyncMicrosoft}
            disabled={isBusy}
            className="flex w-full items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-left transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingMicrosoft || syncing === "microsoft" ? (
              <div className="size-5 shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <svg
                viewBox="0 0 23 23"
                className="size-5 shrink-0"
              >
                <path
                  fill="#f3f3f3"
                  d="M0 0h23v23H0z"
                />
                <path
                  fill="#f35325"
                  d="M1 1h10v10H1z"
                />
                <path
                  fill="#81bc06"
                  d="M12 1h10v10H12z"
                />
                <path
                  fill="#05a6f0"
                  d="M1 12h10v10H1z"
                />
                <path
                  fill="#ffba08"
                  d="M12 12h10v10H12z"
                />
              </svg>
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-white">
                {microsoftConnected
                  ? "Adicionar ao Microsoft Calendar"
                  : "Conectar e adicionar ao Microsoft Calendar"}
              </p>
              {!microsoftConnected && (
                <p className="text-xs text-zinc-500">Será solicitada autorização de acesso</p>
              )}
            </div>
            {microsoftConnected && (
              <span className="shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-400">
                conectado
              </span>
            )}
          </button>

          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isBusy}
            className="w-full text-zinc-400 hover:bg-white/5 hover:text-zinc-200"
          >
            Agora não
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
